import React from 'react';
import { Alert, Badge, Button, Card, CardBody, Input, Table } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { monthLabel, downloadPdf } from '../../utils/invoices';
import { periodOptions, shortDate } from '../../utils/billingOverview';
import { paystackProblem, submitProblem, commissionStatus } from '../../utils/commission';
import { viewingAs } from '../../utils/viewAs';
import { userName } from '../../utils/people';
import '../billing-overview/billing-overview.scss';
import './commission-page.scss';

// A reseller's Commission page (laravel CommissionController, App\Commission): their own companies' invoices (invoiced,
// paid, still owed) for a month, the year so far or all time; their commission (earned, owed now = not invoiced yet,
// invoiced to Mobiliis and awaiting payment, paid) in total, per month and per company; their Paystack payment link;
// Submit invoice (all commission not invoiced yet, as one commission invoice to Mobiliis Ltd); their commission
// invoices with the PDF. The server only ever sends the signed-in reseller's own figures. The super admin, viewing the
// website as a reseller (utils/viewAs), sees that reseller's page as they see it, read only (read_only from the server).
class CommissionPage extends React.Component {
	constructor(props) {
		super(props);
		this.state = { loading: true, error: '', notice: '', data: null, period: null, link: '', linkProblem: '', busy: null };
	}

	componentDidMount() {
		this.load(null);
	}

	reseller() {
		try {
			return localStorage.getItem('admin_level') === 'b';
		} catch (e) {
			return false;
		}
	}

	load(period) {
		if (!this.reseller()) {
			this.setState({ loading: false, error: 'The Commission page is for resellers only.' });
			return;
		}
		this.setState({ loading: true, error: '' });
		const viewing = viewingAs();
		const request = period ? { period } : {};
		if (viewing) {
			request.reseller_id = viewing.id;
		}
		auth.getCommission(request)
			.then((res) => {
				if (res.data.code == 200) {
					this.setState((state) => ({
						loading: false, data: res.data, period: res.data.period,
						// The link as saved, unless the reseller is typing a new one
						link: state.data && state.link !== (state.data.paystack_url || '') ? state.link : (res.data.paystack_url || '')
					}));
				} else {
					this.setState({ loading: false, error: formatServerMessage(res.data.message, 'Commission could not be loaded.') });
				}
			})
			.catch(() => this.setState({ loading: false, error: 'Commission could not be loaded. Please try again.' }));
	}

	saveLink() {
		const problem = paystackProblem(this.state.link);
		if (problem) {
			this.setState({ linkProblem: problem });
			return;
		}
		this.setState({ busy: 'link', linkProblem: '', notice: '', error: '' });
		auth.saveCommissionPaystack({ paystack_url: this.state.link.trim() })
			.then((res) => {
				if (res.data.code == 200) {
					this.setState((state) => ({ busy: null, notice: formatServerMessage(res.data.message, 'Saved.'), link: res.data.paystack_url,
						data: { ...state.data, paystack_url: res.data.paystack_url } }));
				} else {
					this.setState({ busy: null, linkProblem: formatServerMessage(res.data.message, 'The link could not be saved.') });
				}
			})
			.catch(() => this.setState({ busy: null, linkProblem: 'The link could not be saved. Please try again.' }));
	}

	submit() {
		const { data } = this.state;
		if (!window.confirm('Send Mobiliis an invoice for USD ' + data.owed_now_usd + ' of commission? An invoice cannot be changed once it is sent.')) {
			return;
		}
		this.setState({ busy: 'submit', notice: '', error: '' });
		auth.submitCommissionInvoice({})
			.then((res) => {
				if (res.data.code == 200) {
					this.setState({ busy: null, notice: formatServerMessage(res.data.message, 'Your invoice has been sent to Mobiliis.') });
					this.load(this.state.period);
				} else {
					this.setState({ busy: null, error: formatServerMessage(res.data.message, 'The invoice could not be sent.') });
				}
			})
			.catch(() => this.setState({ busy: null, error: 'The invoice could not be sent. Please try again.' }));
	}

	download(invoice) {
		this.setState({ busy: 'pdf' + invoice.id, error: '' });
		auth.getCommissionPdf({ id: invoice.id })
			.then((res) => {
				if (res.data.code == 200) {
					downloadPdf(res.data.file_name, res.data.pdf_base64);
					this.setState({ busy: null });
				} else {
					this.setState({ busy: null, error: formatServerMessage(res.data.message, 'The PDF could not be made.') });
				}
			})
			.catch(() => this.setState({ busy: null, error: 'The PDF could not be made. Please try again.' }));
	}

	renderSubmit(data) {
		const { link, linkProblem, busy } = this.state;
		const readOnly = !!data.read_only;
		const viewing = viewingAs();
		const who = viewing && viewing.user ? userName(viewing.user) : 'the reseller';
		const why = readOnly ? 'Only ' + who + ' can submit an invoice.' : submitProblem(data, data.paystack_url);
		return (
			<div className="commission-submit mb-3">
				{readOnly && (
					<Alert color="info" className="commission-read-only py-2">
						You are seeing {who}'s Commission page as they see it. Only {who} can change the Paystack link or submit an invoice.
					</Alert>
				)}
				<div className="d-flex flex-wrap align-items-end">
					<div className="billing-figure">
						<div className="text-muted small">Owed to you now (not invoiced yet)</div>
						<div className="billing-figure-value">USD {data.owed_now_usd}</div>
						<div className="text-muted small">Your commission: {data.rate_percent}% of each paid invoice</div>
					</div>
					<div className="mb-2">
						<Button color="primary" disabled={!!why || !!busy} title={why || 'Send Mobiliis one invoice for all commission not invoiced yet'}
							onClick={() => this.submit()}>
							{busy === 'submit' ? 'Sending...' : 'Submit invoice'}
						</Button>
						{why && <div className="text-muted small mt-1">{why}</div>}
					</div>
				</div>
				<div className="commission-link mt-2">
					<label className="small mb-1" htmlFor="commission-paystack">
						Your Paystack payment link: Mobiliis pays your invoices by card on this page of your own Paystack account
					</label>
					<div className="d-flex flex-wrap">
						<Input id="commission-paystack" bsSize="sm" type="url" className="mr-2 mb-1" placeholder="https://paystack.com/pay/your-page"
							value={link} invalid={!!linkProblem} readOnly={readOnly} onChange={(e) => this.setState({ link: e.target.value, linkProblem: '' })} />
						{!readOnly && (
							<Button size="sm" color="secondary" className="mb-1" disabled={busy === 'link' || link.trim() === (data.paystack_url || '')}
								onClick={() => this.saveLink()}>
								{busy === 'link' ? 'Saving...' : 'Save'}
							</Button>
						)}
					</div>
					{linkProblem && <div className="text-danger small">{linkProblem}</div>}
					<div className="commission-link-currency text-muted small mt-1">
						Please set your Paystack payment page to US dollars (USD) and leave the amount open. Each invoice's
						"Pay with Paystack" button fills in its own amount.
					</div>
				</div>
			</div>
		);
	}

	renderFigures(sums, label) {
		const figure = (title, value, tone) => (
			<div className="billing-figure">
				<div className="text-muted small">{title}</div>
				<div className={'billing-figure-value' + (tone ? ' text-' + tone : '')}>USD {value}</div>
			</div>
		);
		return (
			<div className="d-flex flex-wrap mb-1">
				{figure('Commission earned, ' + label, sums.earned_usd)}
				{figure('Not invoiced yet', sums.owed_usd)}
				{figure('Invoiced, awaiting payment', sums.invoiced_usd, Number(sums.invoiced_usd) > 0 ? 'warning' : null)}
				{figure('Paid to you', sums.paid_usd, 'success')}
			</div>
		);
	}

	sumsRow(key, name, sums) {
		return (
			<tr key={key}>
				<td>{name}</td>
				<td className="text-right">{sums.earned_usd}</td>
				<td className="text-right">{sums.owed_usd}</td>
				<td className="text-right">{sums.invoiced_usd}</td>
				<td className="text-right">{sums.paid_usd}</td>
			</tr>
		);
	}

	sumsTable(title, rows, empty) {
		return (
			<React.Fragment>
				<h5 className="mt-3">{title}</h5>
				{rows.length === 0 ? <p className="text-muted small">{empty}</p> : (
					<Table size="sm" className="commission-table">
						<thead>
							<tr>
								<th></th>
								<th className="text-right">Earned</th>
								<th className="text-right">Not invoiced yet</th>
								<th className="text-right">Awaiting payment</th>
								<th className="text-right">Paid</th>
							</tr>
						</thead>
						<tbody>{rows}</tbody>
					</Table>
				)}
			</React.Fragment>
		);
	}

	render() {
		const { loading, error, notice, data, period, busy } = this.state;
		return (
			<Card className="billing-overview commission-page">
				<CardBody>
					<div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
						<h4 className="mb-0"><i className="mdi mdi-percent mr-2" />Commission</h4>
						{data && (
							<Input type="select" bsSize="sm" style={{ width: 'auto' }} value={period || ''} aria-label="Period"
								disabled={loading} onChange={(e) => this.load(e.target.value)}>
								{periodOptions(data.months, monthLabel).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
							</Input>
						)}
					</div>
					{error && <Alert color="danger">{error}</Alert>}
					{notice && <Alert color="success" toggle={() => this.setState({ notice: '' })}>{notice}</Alert>}
					{loading && !data && <p>Loading commission...</p>}
					{data && (
						<React.Fragment>
							{this.renderSubmit(data)}
							{this.renderFigures(data.commission.total, data.period_label)}
							<p className="text-muted small mb-2">
								All time: earned USD {data.commission.all_time.earned_usd}, paid to you USD {data.commission.all_time.paid_usd}.
								Commission is earned when your company pays its Mobiliis invoice, on the invoice total.
							</p>

							<h5 className="mt-3">Your companies, {data.period_label}</h5>
							{data.companies.length === 0 ? <p className="text-muted small">You have no companies yet.</p> : (
								<Table size="sm" className="commission-table">
									<thead>
										<tr>
											<th>Company</th>
											<th className="text-right">Invoiced (USD)</th>
											<th className="text-right">Paid</th>
											<th className="text-right">Still owed</th>
										</tr>
									</thead>
									<tbody>
										{data.companies.map((company) => (
											<tr key={company.id}>
												<td>{company.name}</td>
												<td className="text-right">{company.invoiced_usd}</td>
												<td className="text-right">{company.paid_usd}</td>
												<td className={'text-right' + (Number(company.owed_usd) > 0 ? ' text-warning' : '')}>{company.owed_usd}</td>
											</tr>
										))}
										<tr className="font-weight-bold">
											<td>Total</td>
											<td className="text-right">{data.companies_total.invoiced_usd}</td>
											<td className="text-right">{data.companies_total.paid_usd}</td>
											<td className="text-right">{data.companies_total.owed_usd}</td>
										</tr>
									</tbody>
								</Table>
							)}

							{this.sumsTable('Commission by company, ' + data.period_label + ' (USD)',
								data.commission.by_company.map((row) => this.sumsRow(row.company_id, row.name, row)),
								'No commission for ' + data.period_label + '.')}
							{this.sumsTable('Commission by month (USD)',
								data.commission.by_month.map((row) => this.sumsRow(row.period, row.period_label, row)),
								'No commission yet.')}

							<h5 className="mt-3">Your invoices to Mobiliis</h5>
							{data.invoices.length === 0 ? <p className="text-muted small">None yet. Press Submit invoice when you have commission to invoice.</p> : (
								<Table size="sm" className="commission-table">
									<thead>
										<tr>
											<th>Invoice</th>
											<th>Date</th>
											<th>Due</th>
											<th className="text-right">Amount (USD)</th>
											<th>Status</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{data.invoices.map((invoice) => {
											const status = commissionStatus(invoice, shortDate);
											return (
												<tr key={invoice.id}>
													<td>{invoice.number}</td>
													<td>{shortDate(invoice.issue_date)}</td>
													<td>{shortDate(invoice.due_date)}</td>
													<td className="text-right">{invoice.total_usd}</td>
													<td><Badge color={status.tone}>{status.label}</Badge></td>
													<td className="text-right">
														<Button size="sm" color="link" className="p-0" disabled={busy === 'pdf' + invoice.id} onClick={() => this.download(invoice)}>
															Download PDF
														</Button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							)}
						</React.Fragment>
					)}
				</CardBody>
			</Card>
		);
	}
}

export default CommissionPage;
